// ===================================================
// WaySenID: утилиты управления аккаунтами (localStorage)
// Единый модуль для Login.jsx, Register.jsx и др.
// Документация: Plugins/plugins/uam/README.md
// ===================================================

const LAST_EMAIL_KEY = 'wsid_last_email';
const LAST_NAME_KEY = 'wsid_last_name';
const ACCOUNTS_KEY = 'wsid_accounts';
const MAX_ACCOUNTS = 5;

export const safeGet = (key) => {
  try { return localStorage.getItem(key); } catch (_) { return null; }
};

export const safeSet = (key, value) => {
  try { localStorage.setItem(key, value); } catch (_) {}
};

export const safeRemove = (key) => {
  try { localStorage.removeItem(key); } catch (_) {}
};

/**
 * Получить список запомненных аккаунтов
 * @returns {Array<{email: string, display_name: string, last_login: string}>}
 */
export const getAccounts = () => {
  try {
    const raw = safeGet(ACCOUNTS_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(parsed)) return [];
    let changed = false;
    const sanitized = parsed.map((account) => {
      if (!account || typeof account !== 'object') return account;
      if (!Object.prototype.hasOwnProperty.call(account, 'switch_token')) return account;
      const safe = { ...account };
      delete safe.switch_token;
      changed = true;
      return safe;
    }).filter(Boolean);
    if (changed) safeSet(ACCOUNTS_KEY, JSON.stringify(sanitized));
    return sanitized;
  } catch (_) { return []; }
};

/**
 * Сохранить аккаунт в список (макс. MAX_ACCOUNTS)
 * Перемещает существующий наверх или добавляет новый
 * @param {string} email - Email аккаунта
 * @param {string} displayName - Отображаемое имя
 * @param {Object} options - Дополнительные опции из ответа UAM /login
 * @param {boolean} options.quick_login - На auth-origin есть HttpOnly credential быстрого входа
 * @param {string} options.require_password_on_switch - 'always' | 'after_inactivity'
 * @param {number} options.switch_inactivity_days - Дней невхода до запроса пароля (по умолчанию 180)
 */
export const saveAccount = (email, displayName, options = {}) => {
  const accounts = getAccounts().filter(a => a.email !== email);
  const entry = {
    email,
    display_name: displayName || email,
    last_login: new Date().toISOString()
  };
  if (options.quick_login === true) entry.quick_login = true;
  if (options.require_password_on_switch) entry.require_password_on_switch = options.require_password_on_switch;
  if (options.switch_inactivity_days != null) entry.switch_inactivity_days = options.switch_inactivity_days;
  accounts.unshift(entry);
  safeSet(ACCOUNTS_KEY, JSON.stringify(accounts.slice(0, MAX_ACCOUNTS)));
};

/**
 * Удалить аккаунт из списка
 */
export const removeAccount = (email) => {
  const accounts = getAccounts().filter(a => a.email !== email);
  safeSet(ACCOUNTS_KEY, JSON.stringify(accounts));
};

/**
 * Маскировка email для отображения (ab***@domain.com)
 */
export const maskEmail = (email) => {
  if (!email || !email.includes('@')) return email;
  const [local, domain] = email.split('@');
  const masked = local.length <= 2 ? local[0] + '***' : local.slice(0, 2) + '***';
  return masked + '@' + domain;
};

/**
 * Сохранить последний email/имя при успешном входе или регистрации
 * @param {string} email - Email аккаунта
 * @param {string} displayName - Отображаемое имя
 * @param {Object} options - Несекретные настройки из ответа UAM /login
 */
export const saveLastLogin = (email, displayName, options = {}) => {
  const normalizedEmail = (email || '').trim().toLowerCase();
  safeSet(LAST_EMAIL_KEY, normalizedEmail);
  if (displayName) {
    safeSet(LAST_NAME_KEY, displayName);
  }
  saveAccount(normalizedEmail, displayName, options);
};

/**
 * Прочитать последний email
 */
export const getLastEmail = () => safeGet(LAST_EMAIL_KEY);

/**
 * Прочитать последнее имя
 */
export const getLastName = () => safeGet(LAST_NAME_KEY);

/**
 * Очистить данные последнего аккаунта
 */
export const clearLastAccount = () => {
  safeRemove(LAST_EMAIL_KEY);
  safeRemove(LAST_NAME_KEY);
};

export { LAST_EMAIL_KEY, LAST_NAME_KEY, ACCOUNTS_KEY, MAX_ACCOUNTS };
